import { __rest } from "tslib";
import Portal from '@reach/portal';
import FocusLock from 'react-focus-lock';
import styled, { createGlobalStyle, css, keyframes, } from 'styled-components';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useGlobalLocation } from '@ally/federator';
import useKeyPress from '@ally/use-key-press';
import { useClickAway } from '@ally/use-click-away';
import { noop, compose } from '@ally/utilitarian';
import { get, CloseButton } from '@ally/metronome-ui';
const BackdropGlobalStyles = createGlobalStyle(({ hidden }) => `
    body {
      max-height: ${hidden ? '100vh' : 'unset'};
      overflow: ${hidden ? 'hidden !important' : 'auto'};
    }
    html {
      overflow-y: ${hidden ? 'scroll' : 'visible'};
    }
  `);
const Backdrop = styled.div(({ topOffset = 0 }) => {
    return css `
      visibility: visible;
      position: fixed;
      top: ${topOffset}px;
      left: 0;
      height: 100%;
      width: 100%;
      background-color: black;
      opacity: 0.3;
      z-index: 3;
    `;
});
/**
 * Given an entry direction (either left of right), will return the keyframes
 * necessary to animate the sliding drawer. This returns a tuple where the first
 * item if the animation for opening the drawer and the second is used for
 * closing it.
 */
const getSlidingKeyframes = (entry) => [
    keyframes `
    0%   { transform: translateX(${entry === 'left' ? '-' : ''}100%); }
    100% { transform: translateX(0%); }
  `,
    keyframes `
    0%   { transform: translateX(0%); }
    100% { transform: translateX(${entry === 'left' ? '-' : ''}100%); }
  `,
];
const SlidingDrawerWrapper = styled.div(({ isActive, enterFrom = 'right', background, disablePadding, topOffset = 0, }) => {
    const speed = isActive ? 350 : 200;
    const [keyframesOpen, keyframesClose] = getSlidingKeyframes(enterFrom);
    return css `
      align-self: flex-start;
      animation: ${isActive ? keyframesOpen : keyframesClose} ${speed}ms;
      background-color: ${get.colors(background || 'white')};
      box-shadow: -4px 4px 24px -18px rgba(0, 0, 0, 0.05);
      ${enterFrom === 'left' ? 'left: 0' : 'right: 0'};
      min-height: 300px;
      overflow-x: hidden;
      overflow-y: auto;
      padding: ${disablePadding ? '0' : '32px'};
      position: fixed;
      z-index: 4;
      top: ${topOffset}px;
      height: ${topOffset ? `calc(100% - ${topOffset}px)` : '100%'};
      min-width: 300px;
      max-width: 100vw;
      box-sizing: border-box;
    `;
});
const StyledCloseButton = styled(CloseButton)(({ color, theme: { colors } }) => {
    if (!color) {
        return '';
    }
    return `
      path {
        fill: ${colors[color]};
      }
    `;
});
const prop = (name) => (obj) => obj === null || obj === void 0 ? void 0 : obj[name];
const isEqual = (x) => (y) => x === y;
const isClosedState = compose(isEqual('closed'), prop('status'));
const isOpenedState = compose(isEqual('opened'), prop('status'));
const isOpeningState = compose(isEqual('opening'), prop('status'));
const isActiveState = (x) => {
    return isOpenedState(x) || isOpeningState(x);
};
const defaultClickAwayHandler = (close) => close();
const SlidingDrawerContents = (_a) => {
    var { close, children, onClosed, onOpened, isActive, isAriaModal, onClickAway = defaultClickAwayHandler, disableClickAway = false, disableFocusLock = false, hideCloseButton, closeButtonColor, dismissOnRouteChange, topOffset, isBackdropOpen } = _a, others = __rest(_a, ["close", "children", "onClosed", "onOpened", "isActive", "isAriaModal", "onClickAway", "disableClickAway", "disableFocusLock", "hideCloseButton", "closeButtonColor", "dismissOnRouteChange", "topOffset", "isBackdropOpen"]);
    const location = useGlobalLocation({
        strict: false,
    });
    const [isMounted, setMounted] = useState(false);
    const drawerWrapperRef = useRef(null);
    useKeyPress(['Escape'], () => isAriaModal && close('escape-key'));
    const handleClickAway = useMemo(() => disableClickAway
        ? noop
        : (e) => onClickAway(() => close('click-away'), e), [disableClickAway, onClickAway]);
    useClickAway(drawerWrapperRef, handleClickAway);
    useEffect(() => {
        if (!isMounted)
            setMounted(true);
        if (isMounted && dismissOnRouteChange)
            close('location-change');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);
    return (React.createElement(React.Fragment, null,
        React.createElement(SlidingDrawerWrapper, Object.assign({ ref: drawerWrapperRef, isActive: isActive, onClick: (e) => e.stopPropagation(), "data-testid": "sliding-drawer-wrapper", onAnimationEnd: isActive ? onOpened : onClosed }, (isAriaModal ? { role: 'dialog', 'aria-modal': true } : {}), { topOffset: topOffset }, others),
            React.createElement(FocusLock, { disabled: !isAriaModal || !isActive || disableFocusLock },
                children,
                !hideCloseButton && (React.createElement(StyledCloseButton, { color: closeButtonColor, onClick: () => close('close-button'), "data-testid": "drawer-close-icon" })))),
        React.createElement(Backdrop, { "data-testid": "darkened-overlay", onClick: () => close('click-away'), topOffset: topOffset }),
        React.createElement(BackdropGlobalStyles, { hidden: isBackdropOpen })));
};
/**
 * A sliding drawer that handle's its own animation state (opening and closing)
 * and renders it's children into a Portal at the end of the document.
 *
 * Semantics:
 *   - By default the drawer will animate open when rendered.
 *   - Manual "toggling" of the drawer can be handled via the `show` prop which
 *     will programmatically tell the drawer to animate open or close.
 *   - The `show` prop only applies when changed. Once updated, state of the
 *     drawer is turned over to the user.
 *
 * Callbacks:
 *   - onOpening:
 *     Invoked after the state of the drawer has been set to `opening` and the
 *     drawer is animating open.
 *   - OnOpened:
 *     Invoked after the drawer has finished animating open and right before the
 *     state of the drawer is set to `opened`.
 *   - onClosing:
 *     Invoked after the state of the drawer has been set to `closing` and the
 *     drawer is animating shut.
 *   - onClosed:
 *     Invoked after the drawer has finished animating shut and right before the
 *     state of the drawer is set to `closed`.
 *   - onStateChange:
 *     Invoked each time the state of the drawer is changed with the state.
 *
 * @usage
 * <SlidingDrawer
 *   show={isDrawerShown}
 *   onOpened={callback}
 *   onClosed={callback}
 * >
 *   {...}
 * </SlidingDrawer>
 */
const SlidingDrawer = (_a) => {
    var { show = true, children, onOpened = noop, onClosed = noop, onOpening = noop, onClosing = noop, onStateChange = noop, topOffset } = _a, others = __rest(_a, ["show", "children", "onOpened", "onClosed", "onOpening", "onClosing", "onStateChange", "topOffset"]);
    const [drawerState, setState] = useState({
        status: 'closed',
        reason: 'initial-state',
    });
    const [isBackdropOpen, setIsBackdropOpen] = useState(false);
    useEffect(() => {
        onStateChange(drawerState.status);
    }, [drawerState.status]);
    const close = (reason) => {
        if (isActiveState(drawerState)) {
            setState({ status: 'closing', reason });
            setIsBackdropOpen(false);
        }
    };
    const open = () => {
        if (!isActiveState(drawerState)) {
            setState({ status: 'opening' });
            setIsBackdropOpen(true);
        }
    };
    // When the "show" prop is changed, we want to either toggle the drawer open
    // or closed. Note, intentionally leaving out some deps here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => (show ? open() : close('prop-change')), [show]);
    // For `onOpening` and `onClosing`, we want to trigger the invocation of the
    // callback *after* the state change. This is important to allow any focus
    // lock to "let go" so users can change focus via onClosing/onOpening.
    useEffect(() => {
        switch (drawerState.status) {
            case 'opening':
                onOpening();
                break;
            case 'closing':
                onClosing(drawerState.reason);
                break;
            default:
                break;
        }
    }, [drawerState, onOpening, onClosing]);
    if (isClosedState(drawerState))
        return null;
    return (React.createElement(Portal, { type: "sliding-drawer" },
        React.createElement(SlidingDrawerContents, Object.assign({ topOffset: topOffset, close: close, isActive: isActiveState(drawerState), isBackdropOpen: isBackdropOpen, onOpened: compose(() => setState({ status: 'opened' }), onOpened), onClosed: compose(() => setState(prev => ({
                status: 'closed',
                reason: prev.status === 'closing' || prev.status === 'closed'
                    ? prev.reason
                    : 'initial-state',
            })), () => onClosed(drawerState.status === 'closing' ||
                drawerState.status === 'closed'
                ? drawerState.reason
                : 'initial-state')) }, others), children)));
};
export default SlidingDrawer;
//# sourceMappingURL=index.js.map